/**
 * Project Budget and Financial Calculation Helpers
 *
 * Naming Conventions:
 * - Use 'calculate' for mathematical operations and computations
 * - Use 'build' for constructing complex objects
 * - Use 'filter' for array filtering operations
 * - Use 'sum' for simple addition operations
 * - Use 'generate' for creating derived data
 * - Remove redundant words like 'Total' when function already implies totality
 * - Be specific about calculation types (e.g., RoleBudget vs PhaseBudget)
 */

import {
  CalculatedActual,
  DailyActualsAndScheduled,
} from "~/actuals/helpers/actuals-helpers"
import { formatCurrency } from "~/helpers/CurrencyHelper"

import { BudgetedRole } from "~/ProjectDashboard/Header/ProjectBudget/BudgetEditor"
import {
  calculateRoleBudgetFromMinutesAndRate,
  calculateEffectiveRate,
  getPhaseOtherExpenses,
  calculatePhaseBudget,
  roundBudget,
  OtherCost,
} from "@runn/calculations"
import { BudgetMethod } from "~/forms/ProjectBudgetForm/types"

import { calculatePhaseMetrics } from "./PhaseHelper"
import { OTHER_EXPENSES_ROLE, extractRoles } from "./otherCosts"
import { getDefaultRate } from "./rate-helpers"

type ID = string | number

type Project = {
  project_roles: ReadonlyArray<ProjectRole & { id: number }>
  members: ReadonlyArray<{ role_id: number }>
  expenses_budget: number
  other_costs?: OtherCosts
  no_phase_revenue_budget?: number
}

type Role = {
  active: boolean
  id: number
  name: string
}

type PhaseRole = {
  id: number
  role_id: number
  phase_id: number | null
  estimated_minutes: number | null
}

type Phase = {
  id: number
  revenue_budget?: number
  phase_roles?: ReadonlyArray<PhaseRole>
}

type ProjectRate = { role_id: ID; rate: number }

type ProjectRole = {
  id: number
  role_id: ID
  estimated_minutes: number
}

export type RateCard = Readonly<{
  id: number
  name: string | null
  blended_rate: number | null
  blended_rate_card: boolean
  role_charge_out_rates: ReadonlyArray<{
    role_id: number | null
    charge_out_rate: number | null
  }>
}>

export type OtherCosts = ReadonlyArray<OtherCost>

export type ProjectWithBudget = {
  budget_method: BudgetMethod
  phases: ReadonlyArray<Phase>
  project_roles: ReadonlyArray<ProjectRole>
  no_phase_revenue_budget?: number
  rate_card: RateCard
  project_rates: ReadonlyArray<ProjectRate>
  other_costs: OtherCosts
  phase_roles: ReadonlyArray<PhaseRole>
  pricing_model: string
  total_budget: number
}

export const isBillableProject = (pricing_model: string, internal: boolean) =>
  pricing_model !== "nb" && !internal

export const buildBudgetedRolesList = (
  project: Pick<
    Project,
    "project_roles" | "members" | "expenses_budget" | "other_costs"
  >,
  roles: ReadonlyArray<Role>,
  isBillable: boolean,
) => {
  // Important note:
  // project.project_roles can be read as Budgeted Roles in the project.
  // It is not a list of roles in the project.
  const budgetedRoleIds = project.project_roles.map((r) => r.role_id)
  const scheduledRoleIds = project.members.map((r) => r.role_id)
  const hasBudgetedExpenses =
    project.expenses_budget || project.expenses_budget === 0

  const budgetRoles = roles
    .filter(
      (r) => budgetedRoleIds.includes(r.id) || scheduledRoleIds.includes(r.id),
    )
    .map<BudgetedRole>((r) => {
      const existingBudgetedRole = project.project_roles.find(
        (pr) => r.id === pr.role_id,
      )
      const budgetedRole = {
        ...r,
        estimated_minutes: existingBudgetedRole?.estimated_minutes,
        project_role_id: existingBudgetedRole?.id,
        isScheduled: scheduledRoleIds.includes(r.id),
      }

      return budgetedRole
    })

  if (hasBudgetedExpenses || (project.other_costs.length && isBillable)) {
    budgetRoles.push({
      ...OTHER_EXPENSES_ROLE,
      project_role_id: OTHER_EXPENSES_ROLE.id,
      estimated_minutes: project.expenses_budget ?? 0,
      isScheduled: !!project.other_costs.length,
    })
  }

  return budgetRoles
}

export const calculateBudgetAllocation = (
  budgetRoles: ReadonlyArray<BudgetedRole>,
  rates: ReadonlyArray<ProjectRate>,
): number => {
  const { expenses } = extractRoles(budgetRoles)

  // Calculate total from role rates (minutes * rate)
  const roleTotal = rates.reduce((acc, val) => {
    const roleBudget = roundBudget(
      ((budgetRoles.find((br) => br.id === val.role_id)?.estimated_minutes ||
        0) /
        60) *
        val.rate,
    )
    return acc + roleBudget
  }, 0)

  // Add expenses budget (stored directly as dollars, not minutes)
  const expensesBudget = expenses?.estimated_minutes ?? 0

  return roleTotal + expensesBudget
}

/**
 * @deprecated Use calculateProjectBudgetMinutes instead. This function does not handle different budget methods correctly.
 */
export const getBudgetMinutes = (
  project_roles: ReadonlyArray<Pick<ProjectRole, "estimated_minutes">>,
): number => project_roles.reduce((acc, pr) => acc + pr.estimated_minutes, 0)

export const sumPhaseBudgets = (
  phases: ReadonlyArray<{ revenueBudget: number }>,
): number => {
  return phases.reduce((sum, phase) => {
    return sum + phase.revenueBudget
  }, 0)
}

export const calculatePhaseRoleBudget = (
  phaseRoles: ReadonlyArray<{ roleId: number; estimatedMinutes: number }>,
  rateCard: RateCard,
  projectRates: ReadonlyArray<{ roleId: ID; rate: number }>,
): number => {
  return phaseRoles.reduce((sum, phaseRole) => {
    const effectiveRate = calculateEffectiveRate(
      phaseRole.roleId,
      rateCard,
      projectRates.map((pr) => ({
        role_id: Number(pr.roleId),
        rate: pr.rate,
      })),
    )

    const roleBudget = calculateRoleBudgetFromMinutesAndRate(
      phaseRole.estimatedMinutes,
      effectiveRate,
    )

    return sum + roleBudget
  }, 0)
}

/**
 * Calculates the multiplier factor used to adjust revenue for fixed price projects
 * by comparing total budget to total billings
 */
export const getMultiFactor = (
  project: Pick<ProjectWithBudget, "pricing_model" | "total_budget">,
  totalBillings: number,
) =>
  project.pricing_model === "fp" && project.total_budget && totalBillings
    ? project.total_budget / totalBillings
    : 1

export const calculateBudgetWithRemainingByPhase = (
  project: Pick<
    ProjectWithBudget,
    | "budget_method"
    | "project_rates"
    | "rate_card"
    | "phases"
    | "other_costs"
    | "pricing_model"
    | "total_budget"
    | "phase_roles"
  >,
  phase: Phase,
  combinedHours: CalculatedActual[],
) => {
  const budget = calculatePhaseBudget({
    budgetMethod: project.budget_method,
    phase,
    phaseRoles: project.phase_roles,
    rateCard: project.rate_card,
    projectRates: project.project_rates.map((pr) => ({
      role_id: Number(pr.role_id),
      rate: pr.rate,
    })),
  })
  const combinedHoursByPhase = combinedHours.filter(
    (day) => day.phase_id === phase.id,
  )

  const { revenue } = calculatePhaseMetrics({
    hoursByDay: combinedHoursByPhase,
    multiFactor: 1, // Fixed price projects should be handled elsewhere
    otherCosts: getPhaseOtherExpenses(project.other_costs, phase.id),
  })

  return {
    budget,
    budgetRemaining: (budget ?? 0) - revenue,
  }
}

export const calculateTotal = (
  data: DailyActualsAndScheduled[0],
  projectType: string,
  isBillable: boolean,
  multiFactor: number,
  dataType: "combined" | "scheduled",
) => {
  const prefix = dataType === "combined" ? "combined" : "scheduled"

  if (projectType === "revenue") {
    if (isBillable) {
      return data[`${prefix}_billable_amount`] * multiFactor
    }
    return 0
  }

  if (projectType === "costs") {
    return data[`${prefix}_cost`]
  }

  if (projectType === "hours") {
    if (isBillable) {
      return data[`${prefix}_billable_minutes`] / 60
    }
    return data[`${prefix}_nonbillable_minutes`] / 60
  }
}

// Legacy function names for backward compatibility
export const calculateCombinedTotal = (
  data: DailyActualsAndScheduled[0],
  projectType: string,
  isBillable: boolean,
  multiFactor: number,
) => calculateTotal(data, projectType, isBillable, multiFactor, "combined")

export const calculateScheduledTotal = (
  data: DailyActualsAndScheduled[0],
  projectType: string,
  isBillable: boolean,
  multiFactor: number,
) => calculateTotal(data, projectType, isBillable, multiFactor, "scheduled")

export const generateBudgetWarnings = (
  project: Project & {
    pricing_model: string
    client: { internal: boolean }
    total_budget: number
    project_rates: ReadonlyArray<ProjectRate>
  },
  roles: ReadonlyArray<Role>,
) => {
  const warnings = []

  const budgetRoles = buildBudgetedRolesList(
    project,
    roles,
    isBillableProject(project.pricing_model, project.client.internal),
  )
  const totalAllocated = calculateBudgetAllocation(
    budgetRoles,
    project.project_rates,
  )
  const budgetAllocationDifference = project.total_budget - totalAllocated

  if (budgetAllocationDifference < 0) {
    warnings.push(
      `Budget is overallocated by ${formatCurrency(
        Math.abs(budgetAllocationDifference),
      )}`,
    )
  } else if (budgetAllocationDifference > 0) {
    warnings.push(
      `Budget is underallocated by ${formatCurrency(
        Math.abs(budgetAllocationDifference),
      )}`,
    )
  }

  const hasRolesWithoutRates = budgetRoles
    .filter((role) => role.project_role_id !== OTHER_EXPENSES_ROLE.id)
    .some(
      (role) =>
        project.project_rates.find(
          (pr) => pr.role_id === role.id && pr.rate === 0,
        ) || !project.project_rates.find((pr) => pr.role_id === role.id),
    )

  if (hasRolesWithoutRates && project.pricing_model !== "nb") {
    warnings.push("Project rates are set to $0")
  }

  return warnings
}

export const calculateCostFromSummary = (
  summary: ReadonlyArray<{
    role_id: number
    total_billable_minutes: number
  }>,
  projectRates: ReadonlyArray<{ role_id: ID; rate: number }>,
  rateCard: RateCard,
): number => {
  const rateMap = new Map(
    projectRates.map((rate) => [rate.role_id, rate.rate ?? 0]),
  )

  return summary.reduce((acc, summaryItem) => {
    const rate =
      rateMap.get(summaryItem.role_id) ??
      getDefaultRate(rateCard, summaryItem.role_id)
    return acc + (summaryItem.total_billable_minutes / 60) * rate
  }, 0)
}

export default {
  calculateBudgetAllocation,
  getBudgetMinutes,
  isBillableProject,
}
